/**
 * =============================================================================
 * PHS COMMAND SIDEBAR - UNIFIED Enterprise Navigation
 * =============================================================================
 * Single unified sidebar for ALL PHS Operations pages.
 * Dark premium design with complete operational navigation.
 * 
 * MASTER SIDEBAR STRUCTURE:
 * - COMMAND: Dashboard, Pipeline Board
 * - JOB LIFECYCLE: All granular job statuses
 * - PPM & COMPLIANCE: PPM Schedule, Library, Compliance, Expiries
 * - ASSETS: Parks, Plots, Asset Register
 * - CONTRACTORS: Management, Performance, Compliance
 * - COMMERCIAL: Benchmarking, Rate Library, Financial Dashboard
 * - KNOWLEDGE & SUPPORT: User Guides, SOPs, Governance
 */

import React, { useState, useMemo } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { 
  LayoutDashboard, Briefcase, Clock, AlertTriangle, RotateCcw,
  Calendar, Shield, AlertCircle, Building2, MapPin, Database,
  BarChart3, BookOpen, DollarSign, TrendingUp, Users,
  FileText, Receipt, CreditCard, PieChart, FileBarChart,
  CheckSquare, Bell, Settings, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, Home, X, Send, Camera, GitBranch,
  Layers, ClipboardList, GraduationCap, FileCheck, Scale,
  Wrench, Award, BadgeCheck, HelpCircle, XCircle, CircleDollarSign
} from 'lucide-react';
import { TOKENS } from './PHSCommandLayout';

// =============================================================================
// MASTER SIDEBAR NAVIGATION STRUCTURE
// =============================================================================
// PHS OPERATIONS - LIVE FM HELPDESK PLATFORM
// This is an FM Operations Centre, NOT a SaaS dashboard
// =============================================================================
const SIDEBAR_GROUPS = [
  // =========================================================================
  // COMMAND - Primary navigation (Dashboard / Command Centre)
  // =========================================================================
  {
    id: 'command',
    name: 'COMMAND',
    items: [
      { name: 'Helpdesk Dashboard', path: '/phs/helpdesk', icon: LayoutDashboard },
      { name: 'Pipeline Board', path: '/phs/helpdesk/board', icon: GitBranch },
    ]
  },
  // =========================================================================
  // PRIORITY - Urgent operational views
  // =========================================================================
  {
    id: 'priority',
    name: 'PRIORITY',
    items: [
      { name: 'SLA Breaches', path: '/phs/helpdesk?sla=breached', icon: AlertTriangle, countKey: 'sla_breaches', alert: true },
      { name: 'Unassigned Jobs', path: '/phs/helpdesk?unassigned=true', icon: AlertCircle, countKey: 'unassigned' },
      { name: 'Follow-On Required', path: '/phs/helpdesk?followon=required', icon: RotateCcw, countKey: 'follow_on' },
    ]
  },
  // =========================================================================
  // JOB LIFECYCLE - All granular statuses (filtered views)
  // =========================================================================
  {
    id: 'job_lifecycle',
    name: 'JOB LIFECYCLE',
    collapsible: true,
    defaultCollapsed: false,
    items: [
      { name: 'New Enquiries', path: '/phs/helpdesk?status=Job%20Created', icon: Briefcase, countKey: 'new_enquiries' },
      { name: 'Assigned', path: '/phs/helpdesk?status=Assigned', icon: Users, countKey: 'assigned' },
      { name: 'Awaiting Quote', path: '/phs/helpdesk?status=Awaiting%20Quote', icon: Clock, countKey: 'awaiting_quote' },
      { name: 'Quote Review', path: '/phs/helpdesk?status_list=Quote%20Awaiting%20Review,Quote%20Queried', icon: FileText, countKey: 'quote_review' },
      { name: 'Approved', path: '/phs/helpdesk?status=Approved', icon: CheckSquare, countKey: 'approved' },
      { name: 'Scheduled', path: '/phs/helpdesk?status=Scheduled', icon: Calendar, countKey: 'scheduled' },
      { name: 'In Progress', path: '/phs/helpdesk?status=In%20Progress', icon: Wrench, countKey: 'in_progress' },
      { name: 'Awaiting Invoice', path: '/phs/helpdesk?status=Awaiting%20Invoice', icon: Receipt, countKey: 'awaiting_invoice' },
      { name: 'Invoiced', path: '/phs/helpdesk?status_list=Invoice%20Submitted,Invoice%20Queried', icon: FileText, countKey: 'invoiced' },
      { name: 'On Hold', path: '/phs/helpdesk?status=On%20Hold', icon: AlertTriangle, countKey: 'on_hold' },
    ]
  },
  // =========================================================================
  // WORKSPACES - Core operational workspaces
  // =========================================================================
  {
    id: 'workspaces',
    name: 'WORKSPACES',
    items: [
      { name: 'Assets', path: '/phs/assets', icon: Building2 },
      { name: 'Contractors', path: '/phs/contractors', icon: Users },
      { name: 'Finance', path: '/phs/finance', icon: DollarSign },
    ]
  },
  // =========================================================================
  // PPM & COMPLIANCE
  // =========================================================================
  {
    id: 'ppm_compliance',
    name: 'PPM & COMPLIANCE',
    collapsible: true,
    defaultCollapsed: true,
    items: [
      { name: 'PPM Schedule', path: '/phs/assets?tab=ppm', icon: Calendar, countKey: 'ppm_count' },
      { name: 'Compliance Register', path: '/phs/assets?tab=compliance', icon: Shield, countKey: 'compliance_count' },
      { name: 'Expiring Soon', path: '/phs/assets?tab=compliance&filter=expiring', icon: AlertCircle, countKey: 'expiring_compliance' },
    ]
  },
  // =========================================================================
  // REPORTS & TOOLS
  // =========================================================================
  {
    id: 'reports',
    name: 'REPORTS & TOOLS',
    collapsible: true,
    defaultCollapsed: true,
    items: [
      { name: 'Operational Health', path: '/phs/health', icon: TrendingUp },
      { name: 'Reports', path: '/phs/reports', icon: FileBarChart },
      { name: 'Benchmarking', path: '/phs/benchmarking', icon: BarChart3 },
      { name: 'Rate Library', path: '/phs/rate-library', icon: BookOpen },
    ]
  },
  // =========================================================================
  // GOVERNANCE
  // =========================================================================
  {
    id: 'governance',
    name: 'GOVERNANCE',
    collapsible: true,
    defaultCollapsed: true,
    items: [
      { name: 'User Guide', path: '/phs/governance/user-guide', icon: GraduationCap },
      { name: 'SOPs', path: '/phs/governance/sops', icon: FileCheck },
      { name: 'Governance Hub', path: '/phs/governance', icon: Scale },
    ]
  },
];

export default function PHSCommandSidebar({ 
  collapsed = false, 
  onToggle,
  jobCounts = {},
  user = null,
  isMobile = false,
  onClose = () => {}
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Initialize collapsed state based on group defaults
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const initial = {};
    SIDEBAR_GROUPS.forEach(group => {
      if (group.collapsible && group.defaultCollapsed) {
        initial[group.id] = true;
      }
    });
    return initial;
  });

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Check if a nav item is active (matching path and query params)
  const isActive = (itemPath) => {
    const [basePath, queryString] = itemPath.split('?');
    const currentPath = location.pathname;
    const currentSearch = location.search;
    
    // Check if base paths match
    if (basePath === currentPath || currentPath.startsWith(basePath + '/')) {
      // If item has query params, check if they match
      if (queryString) {
        const itemParams = new URLSearchParams(queryString);
        const currentParams = new URLSearchParams(currentSearch);
        
        // Check each param in the item
        for (const [key, value] of itemParams.entries()) {
          if (currentParams.get(key) !== value) {
            return false;
          }
        }
        return true;
      }
      // No query params required, path match is enough
      // But only if there are no status/view params in current URL
      if (!currentSearch || (!currentSearch.includes('status=') && !currentSearch.includes('view='))) {
        return basePath === currentPath;
      }
      return false;
    }
    return false;
  };

  const handleLinkClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="phs-command-sidebar">
      {/* Header - Park Home Support Branding */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        {(!collapsed || isMobile) ? (
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white text-sm"
              style={{ backgroundColor: TOKENS.colors.primaryTeal }}
            >
              PHS
            </div>
            <div>
              <div className="text-white font-semibold text-sm">Park Home Support</div>
              <div className="text-xs text-slate-400">Helpdesk Command Centre</div>
            </div>
          </div>
        ) : (
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto font-bold text-white text-sm"
            style={{ backgroundColor: TOKENS.colors.primaryTeal }}
          >
            PHS
          </div>
        )}
        
        {/* Mobile close button */}
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition"
            data-testid="sidebar-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-slate-700">
        {SIDEBAR_GROUPS.map((group) => {
          const isGroupCollapsed = collapsedGroups[group.id];
          const isCollapsible = group.collapsible;
          
          return (
            <div key={group.id} className="mb-2">
              {/* Group Header */}
              {(!collapsed || isMobile) && (
                <button
                  onClick={() => isCollapsible && toggleGroup(group.id)}
                  className={`w-full flex items-center justify-between px-4 py-2 text-[10px] font-bold tracking-wider text-slate-500 hover:text-slate-300 transition ${
                    isCollapsible ? 'cursor-pointer' : 'cursor-default'
                  }`}
                  data-testid={`sidebar-group-${group.id}`}
                >
                  <span>{group.name}</span>
                  {isCollapsible && (
                    isGroupCollapsed ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronUp className="w-3 h-3" />
                    )
                  )}
                </button>
              )}

              {/* Group Items */}
              {!isGroupCollapsed && (
                <div className="space-y-0.5 px-2">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const count = item.countKey ? (jobCounts[item.countKey] || 0) : null;

                    return (
                      <NavLink
                        key={item.path + item.name}
                        to={item.path}
                        onClick={handleLinkClick}
                        data-testid={`sidebar-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg transition-all group ${
                          active
                            ? 'bg-gradient-to-r from-teal-600/30 to-teal-600/10 text-white border-l-2 border-teal-400'
                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }`}
                        title={collapsed && !isMobile ? item.name : undefined}
                      >
                        <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-teal-400' : ''}`} />
                        {(!collapsed || isMobile) && (
                          <>
                            <span className="text-sm flex-1 truncate">{item.name}</span>
                            {count !== null && count > 0 && (
                              <span 
                                className="text-[10px] min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full font-semibold"
                                style={{ 
                                  backgroundColor: active ? TOKENS.colors.primaryTeal : 'rgba(255,255,255,0.15)',
                                  color: 'white'
                                }}
                              >
                                {count > 99 ? '99+' : count}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer - Collapse Toggle (Desktop only) */}
      {!isMobile && (
        <div className="p-3 border-t border-white/10">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition text-xs"
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
